import { PrimaryButtonMT, PrimaryButtonMTV2, RejectButtonMT, RoundButton } from '@/components/buttons/Buttons';
import { BlockUI } from 'primereact/blockui';
import { Dialog } from 'primereact/dialog';
import { ProgressSpinner } from 'primereact/progressspinner';
import { classNames } from 'primereact/utils';
import { useCallback, useEffect, useState } from 'react';
import AddExistingPeopleForm from '../components/group/AddExistingPeopleForm';
import style from './group.module.css';
import { ENDPOINTS } from '@/constants/endpoints';
import { useQuery } from '@tanstack/react-query';
import { BaseResponse, GenericResponse, ListRequest, ListResponse } from '@/common/common_utils';
import { X4bEmployeeListItem } from '@/types';
import { axiosInstance } from '@/services/axios.service';
import { useMutation } from '@tanstack/react-query';
import { showNotification } from '@/modules/notification/engine/notification.library';
import Spacing from '@/components/container/Spacing';
import SearchBarInput from '@/components/form-inputs/SearchBarInput';
import { useModal } from '@ebay/nice-modal-react';

interface Props {
	groupId: number;
}

interface InviteExistingRequest {
	groupId: number;
	peopleIds: number[];
}

const AddGroupExistingPeopleScreen = ({ groupId }: Props) => {
	const modal = useModal();
	const [blocked, setBlocked] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [searchValue, setSearchValue] = useState("");
	const [selectedPeople, setSelectedPeople] = useState<X4bEmployeeListItem[]>([]);
	const [filteredPeople, setFilteredPeople] = useState<X4bEmployeeListItem[]>([]);

	const sortAndSlice = useCallback((list: X4bEmployeeListItem[]) =>
		[...list].sort((a, b) => a.FullName.localeCompare(b.FullName)).slice(0, 3),
		[]
	);

	const { data: people = [] } = useQuery<X4bEmployeeListItem[]>({
		queryKey: ['AddGroupExistingPeopleScreen.GetEmployeeList'],
		enabled: modal.visible,
		queryFn: async () => {
			const response = await axiosInstance.post<GenericResponse<ListResponse<X4bEmployeeListItem>>>(
				ENDPOINTS['/api/X4bEmployee/GetEmployeeList'],
				{ Skip: 0, Take: 1000 } as ListRequest
			);
			return response.data.Data.Items.sort((a, b) => a.FullName.localeCompare(b.FullName));
		}
	});

	const handleSearch = (value: string) => {
		setSearchValue(value);
		const results = value.trim()
			// show all matches include selected
			? people.filter(p => p.FullName.toLowerCase().includes(value.toLowerCase()))
			// show all people exclude selected
			: people.filter(p => !selectedPeople.some(sp => sp.Id === p.Id))
		setFilteredPeople(sortAndSlice(results));
	};

	useEffect(() => {
		handleSearch(searchValue);
	}, [people, sortAndSlice, selectedPeople, searchValue]);

	const inviteMutation = useMutation({
		mutationFn: async () => {
			await axiosInstance.post<BaseResponse>(ENDPOINTS['/api/X4bGroup/InviteExisting'], {
				groupId,
				peopleIds: selectedPeople.map(p => p.Id),
			} as InviteExistingRequest);
		},
		onSuccess: () => {
			setIsLoading(false);
			setBlocked(false);
			modal.resolve();
			modal.remove();
		},
		onError: () => {
			setIsLoading(false);
			setBlocked(false);
			showNotification('Error while adding people in the group');
		}
	});

	const handleSubmit = () => {
		setIsLoading(true);
		inviteMutation.mutate();
	};

	const renderHeader = (blocked: boolean) => (
		<div className={classNames('space-y-4', { 'opacity-10': blocked })}>
			<p className="text-headline-small text-onSurface font-normal">Add existing people</p>
			<p className="text-title-medium text-onSurfaceVariant font-normal">
				Adding existing people will remove them from their current group.
			</p>
		</div>
	);

	const renderFooter = (blocked: boolean, onAdd: () => void) => (
		<div className={classNames('flex justify-end gap-2', { 'opacity-10': blocked })}>
			<RejectButtonMT id="cancel-update-employee" label="Cancel" onClick={() => modal.remove()} />
			<Spacing width="w-2" />
			<PrimaryButtonMT id="update-employee" label="Add People" onClick={onAdd} disabled={selectedPeople.length === 0} />
		</div>
	);

	const blockTemplate = isLoading ? (
		<div className={classNames('flex h-full w-full justify-content-center', style.groupBlock)}>
			<ProgressSpinner className="self-center" pt={{ circle: { className: style.groupSpinner } }} />
		</div>
	) : (
		<div className={classNames('flex flex-col justify-center p-10 h-full space-y-6', style.groupBlock)}>
			<div className="space-y-2">
				<h1 className="text-neutral17 text-headline-large">Updating a group</h1>
				<p className="text-body-medium text-neutral17">
					When you update a group, the access and privileges of the group members will be updated as well.
					Are you sure you want to procees?
				</p>
			</div>
			<div className="flex flex-col gap-4">
				<PrimaryButtonMTV2 label="Yes, Proceed" id="proceed-add-people-group" onClick={handleSubmit} />
				<RejectButtonMT label="No, Please cancel these changes" id="cancel-add-people-group" className={classNames('h-12')} onClick={() => setBlocked(false)} />
			</div>
		</div>
	);

	return (
		<Dialog
			onHide={() => modal.remove()}
			visible={modal.visible}
			header={renderHeader(blocked)}
			footer={renderFooter(blocked, () => setBlocked(true))}
			closable={!blocked}
			pt={{
				root: { className: classNames(style['group-people-modal-top'], style['group-people-modal-bottom'], style['group-people-modal-root'], style['group-people-modal']) },
				header: { className: classNames(style['group-people-modal-header'], style['group-people-modal-top'], style['group-people-modal']) },
				content: { className: classNames(style['group-people-modal-content'], style['group-people-modal']) },
				footer: { className: classNames(style['group-people-modal-bottom'], style['group-people-modal-footer'], style['group-people-modal']) },
			}}
		>
			<BlockUI pt={{ root: { className: style.groupBlockRoot } }} blocked={blocked} template={blockTemplate}>
				<div>
					<div className="px-6 py-3">
						<SearchBarInput
							placeholder="Search for an individual"
							onChange={(e) => handleSearch(e.target.value)}
							className="!bg-white"
						/>
					</div>
					<AddExistingPeopleForm allPeople={people} peopleToShow={filteredPeople} onSelectedPeopleChange={setSelectedPeople} />
				</div>
			</BlockUI>
		</Dialog>
	);
};

export default AddGroupExistingPeopleScreen;
